import { FC } from 'react';

import { ChannelBalanceSummary } from '@shared/api/giftChannelLedger/giftChannelLedger.interface';
import {
  DELIVERY_CHANNEL_CONFIG,
  DELIVERY_CHANNEL_LIST,
  DeliveryChannel,
  formatDeliveryChannelSelectLabel,
} from '@shared/api/giftCoupon/giftCoupon.interface';

import {
  CHANNEL_LIST_DISCOUNT_RATE,
  listDiscountLabel,
} from '../utils/chargeEstimate.utils';
import {
  DeliverModalState,
  LastCopiedProduct,
  RedemptionProductPricing,
} from '../hooks/useRedemptionPage';

const DELIVERY_CHANNEL_SELECT_OPTIONS: Array<{
  value: DeliveryChannel | '';
  label: string;
}> = [
  { value: '', label: '발송 매체 선택' },
  ...DELIVERY_CHANNEL_LIST.map((ch) => ({
    value: ch,
    label: formatDeliveryChannelSelectLabel(ch),
  })),
];

interface Props {
  deliverModal: DeliverModalState;
  setDeliverModal: (modal: DeliverModalState | null) => void;
  onConfirm: () => void;
  onReject: (couponId: string) => void;
  /** 충전금 미러 원장 잔액 — null이면(구서버 404) 잔액 영역 미노출 */
  channelBalances?: ChannelBalanceSummary[] | null;
  /** 발송 대상 상품 — 어떤 상품을 발주했는지 못박고, 예상 도매가를 제안한다 */
  product?: RedemptionProductPricing | null;
  /** 마지막으로 번호를 복사한 상품 — 대상과 다르면 오발주 경고 */
  lastCopiedProduct?: LastCopiedProduct | null;
}

export const DeliverModal: FC<Props> = ({
  deliverModal,
  setDeliverModal,
  onConfirm,
  onReject,
  channelBalances,
  product,
  lastCopiedProduct,
}) => {
  const count = deliverModal.couponIds.length;
  // 매체에서 산 상품과 발송 처리하는 상품이 어긋나면 되돌릴 수 없는 오발주다.
  // 복사한 상품과 다르면 여기서 잡아준다. (복사 없이 연 경우는 경고 없음)
  const copyMismatch =
    !!product && !!lastCopiedProduct && lastCopiedProduct.key !== product.key;
  const wholeNum = Number(deliverModal.priceWhole);
  const hasValidWhole =
    deliverModal.priceWhole !== '' && !isNaN(wholeNum) && wholeNum > 0;
  const totalAmount = hasValidWhole ? wholeNum * count : 0;

  // 선택 매체의 미러 잔액 — 발송 직전 실수(도매가 오입력·충전 부족)를 잡는 핵심 대조값
  const selectedBalance =
    deliverModal.deliveryChannel && channelBalances
      ? channelBalances.find((b) => b.channel === deliverModal.deliveryChannel)
      : undefined;
  const afterBalance =
    selectedBalance && hasValidWhole ? selectedBalance.balance - totalAmount : null;

  // 예상 도매가 제안 — 상품이 고정돼 있으니 두 방식 단가를 그대로 꺼내 쓴다.
  // 감액률은 지금 고른 매체 기준(미선택이면 예측 매체)이라 매체를 바꾸면 같이 바뀐다.
  const rateChannel = deliverModal.deliveryChannel || product?.recentChannel || null;
  const listRate = rateChannel ? CHANNEL_LIST_DISCOUNT_RATE[rateChannel] : undefined;
  const listUnitPrice =
    product?.listPrice && listRate !== undefined
      ? Math.round(product.listPrice * (1 - listRate))
      : null;

  const suggestions: Array<{ label: string; hint: string; price: number }> = [];
  if (product?.recentUnitPrice) {
    suggestions.push({
      label:
        product.recentPriceSource === 'recentDelivered' ? '최근 발송가' : '카탈로그가',
      hint: product.recentChannel
        ? DELIVERY_CHANNEL_CONFIG[product.recentChannel].label
        : '매체 미분류',
      price: product.recentUnitPrice,
    });
  }
  if (listUnitPrice && rateChannel) {
    suggestions.push({
      label: listDiscountLabel(rateChannel) ?? '정가 기준',
      hint: `정가 ${product?.listPrice?.toLocaleString()}원`,
      price: listUnitPrice,
    });
  }

  // 매체 제안도 두 방식 — 도매사이트 URL / 직전 발송.
  // 둘이 일치하면 열 때 이미 채워져 있고(agreedDeliveryChannel), 엇갈리면 근거를 보여주고 고르게 한다.
  const channelHints: Array<{ label: string; channel: DeliveryChannel }> = [];
  if (product?.urlChannel) {
    channelHints.push({ label: '발송 URL', channel: product.urlChannel });
  }
  if (product?.recentDeliveredChannel) {
    channelHints.push({ label: '직전 발송', channel: product.recentDeliveredChannel });
  }
  const channelConflict =
    !!product?.urlChannel &&
    !!product?.recentDeliveredChannel &&
    product.urlChannel !== product.recentDeliveredChannel;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={() => setDeliverModal(null)} />
      <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-gray-900">
          발송 처리
          {deliverModal.isBulk ? ` (${deliverModal.couponIds.length}건)` : ''}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          가격 정보를 입력한 후 발송 처리하세요.
        </p>

        {/* 발주 상품 — 매체에서 실제로 구매한 상품과 같은지 대조하는 자리 */}
        {product && (
          <div
            className={`mt-3 rounded-lg border px-3 py-2 ${
              copyMismatch ? 'border-red-300 bg-red-50' : 'border-indigo-200 bg-indigo-50'
            }`}
          >
            <p
              className={`text-[11px] font-semibold ${
                copyMismatch ? 'text-red-500' : 'text-indigo-500'
              }`}
            >
              발주 상품
            </p>
            <p
              className={`break-keep text-base font-bold leading-snug ${
                copyMismatch ? 'text-red-900' : 'text-indigo-900'
              }`}
            >
              {product.label}
            </p>
            {copyMismatch && (
              <p className="mt-1.5 text-xs font-bold text-red-700">
                ! 마지막으로 복사한 번호는 &quot;{lastCopiedProduct?.label}&quot;(
                {lastCopiedProduct?.count}건)입니다. 매체에서 구매한 상품이 맞는지 확인 후
                처리하세요.
              </p>
            )}
          </div>
        )}

        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              발송 매체 (deliveryChannel) <span className="text-red-500">*</span>
            </label>
            <select
              value={deliverModal.deliveryChannel}
              onChange={(e) =>
                setDeliverModal({
                  ...deliverModal,
                  deliveryChannel: e.target.value as DeliveryChannel | '',
                })
              }
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {DELIVERY_CHANNEL_SELECT_OPTIONS.map((opt) => (
                <option
                  key={opt.value === '' ? '__none__' : opt.value}
                  value={opt.value}
                  disabled={opt.value === ''}
                >
                  {opt.label}
                </option>
              ))}
            </select>

            {/* 매체 근거 — 두 방식이 일치하면 위 select가 이미 채워져 있다 */}
            {channelHints.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-gray-400">근거</span>
                {channelHints.map((h) => {
                  const isApplied = deliverModal.deliveryChannel === h.channel;
                  return (
                    <button
                      key={h.label}
                      type="button"
                      onClick={() =>
                        setDeliverModal({ ...deliverModal, deliveryChannel: h.channel })
                      }
                      title={`${h.label} 기준 — 클릭 시 매체 선택`}
                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${
                        isApplied
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-gray-400">{h.label}</span>
                      <span className="font-bold">
                        {DELIVERY_CHANNEL_CONFIG[h.channel].label}
                      </span>
                    </button>
                  );
                })}
                {channelConflict ? (
                  <span className="text-xs font-bold text-amber-700">
                    · 근거가 엇갈립니다. 직접 확인하세요.
                  </span>
                ) : (
                  channelHints.length === 2 && (
                    <span className="text-xs text-gray-400">· 두 근거 일치</span>
                  )
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              판매가 (priceRetail)
            </label>
            <div className="mt-1 flex items-center rounded-md border bg-gray-50 px-3 py-2 text-sm text-gray-500">
              {deliverModal.priceRetail.toLocaleString()}원
              <span className="ml-2 text-xs text-gray-400">(자동)</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              도매가 (priceWhole)
            </label>
            <input
              type="number"
              value={deliverModal.priceWhole}
              onChange={(e) =>
                setDeliverModal({
                  ...deliverModal,
                  priceWhole: e.target.value,
                  // 도매가가 바뀌면 총액도 바뀌므로 금액 확인을 다시 받아야 함
                  amountConfirmed: false,
                })
              }
              placeholder="도매가 입력"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />

            {/* 예상 도매가 — 클릭하면 그대로 입력된다 (건당 단가, 총액은 아래 발주비용) */}
            {suggestions.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-gray-400">예상</span>
                {suggestions.map((s) => {
                  const isApplied = wholeNum === s.price;
                  return (
                    <button
                      key={s.label}
                      type="button"
                      onClick={() =>
                        setDeliverModal({
                          ...deliverModal,
                          priceWhole: String(s.price),
                          amountConfirmed: false,
                        })
                      }
                      title={`${s.label} (${s.hint}) — 클릭 시 도매가 입력 · 총 ${(
                        s.price * count
                      ).toLocaleString()}원`}
                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${
                        isApplied
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-gray-400">{s.label}</span>
                      <span className="font-bold">{s.price.toLocaleString()}원</span>
                    </button>
                  );
                })}
                {suggestions.length === 2 && suggestions[0] && suggestions[1] && (
                  <span className="text-xs text-gray-400">
                    {suggestions[0].price === suggestions[1].price
                      ? '· 두 방식 동일'
                      : `· 차이 ${Math.abs(
                          suggestions[0].price - suggestions[1].price,
                        ).toLocaleString()}원`}
                  </span>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              리베이트 (priceRebate){' '}
              <span className="text-xs font-normal text-gray-400">
                (선택 · 미입력 시 0)
              </span>
            </label>
            <input
              type="number"
              value={deliverModal.priceRebate}
              onChange={(e) =>
                setDeliverModal({ ...deliverModal, priceRebate: e.target.value })
              }
              placeholder="0"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
            {hasValidWhole ? (
              <>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-medium text-gray-700">총 발주비용</span>
                  <span className="text-lg font-bold text-amber-700">
                    {totalAmount.toLocaleString()}원
                  </span>
                </div>
                <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-800">
                  <input
                    type="checkbox"
                    checked={deliverModal.amountConfirmed}
                    onChange={(e) =>
                      setDeliverModal({
                        ...deliverModal,
                        amountConfirmed: e.target.checked,
                      })
                    }
                    className="h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>이 금액이 맞습니다.</span>
                </label>
              </>
            ) : (
              <p className="text-sm text-gray-500">도매가 입력 필요</p>
            )}
          </div>

          {selectedBalance && (
            <div
              className={`rounded-md border p-3 text-sm ${
                afterBalance !== null && afterBalance < 0
                  ? 'border-red-300 bg-red-50'
                  : 'border-indigo-200 bg-indigo-50/60'
              }`}
            >
              <div className="flex items-baseline justify-between">
                <span className="text-gray-600">
                  현재 {DELIVERY_CHANNEL_CONFIG[selectedBalance.channel].label} 잔액
                </span>
                <span
                  className={`font-semibold ${
                    selectedBalance.balance < 0 ? 'text-red-600' : 'text-gray-900'
                  }`}
                >
                  {selectedBalance.balance.toLocaleString()}원
                </span>
              </div>
              {afterBalance !== null ? (
                <div className="mt-1 flex items-baseline justify-between">
                  <span className="text-gray-600">발송 후 잔액</span>
                  <span
                    className={`font-bold ${
                      afterBalance < 0 ? 'text-red-600' : 'text-indigo-700'
                    }`}
                  >
                    {afterBalance.toLocaleString()}원
                  </span>
                </div>
              ) : (
                <p className="mt-1 text-xs text-gray-400">
                  도매가를 입력하면 발송 후 잔액이 표시됩니다.
                </p>
              )}
              {afterBalance !== null && afterBalance < 0 && (
                <p className="mt-1 text-xs font-medium text-red-600">
                  잔액 부족 — 도매가 오입력이 아닌지, 충전·입금 기록이 빠지지 않았는지
                  확인하세요. (발송이 차단되지는 않습니다)
                </p>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          {!deliverModal.isBulk ? (
            <button
              onClick={() => {
                const couponId = deliverModal.couponIds[0] ?? '';
                setDeliverModal(null);
                onReject(couponId);
              }}
              className="text-xs text-orange-600 hover:text-orange-800 hover:underline"
            >
              발송불가 처리
            </button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <button
              onClick={() => setDeliverModal(null)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              취소
            </button>
            <button
              onClick={onConfirm}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              발송 처리
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
